import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import {
  fetchCellDialogs,
  fetchConceptCard,
  fetchConcepts,
  fetchHierarchy,
  fetchMatrix,
  fetchStandardsMatrix,
  fetchTransitions,
} from './api';
import type {
  StrategistCellDialogsRequest,
  StrategistConceptCardRequest,
  StrategistConceptsRequest,
  StrategistHierarchyRequest,
  StrategistMatrixRequest,
  StrategistStandardsMatrixRequest,
  StrategistTransitionsRequest,
} from '../types';

/**
 * Ключи кэша react-query раздела «Стратег».
 * Тело запроса целиком входит в ключ — смена фильтров/периода/цели триггерит рефетч.
 */
export const strategistKeys = {
  all: [API_TAGS.STRATEGIST] as const,
  cellDialogs: (body: StrategistCellDialogsRequest) => [...strategistKeys.all, 'cell-dialogs', body] as const,
  concept: (body: StrategistConceptCardRequest) => [...strategistKeys.all, 'concept', body] as const,
  concepts: (body: StrategistConceptsRequest) => [...strategistKeys.all, 'concepts', body] as const,
  hierarchy: (body: StrategistHierarchyRequest) => [...strategistKeys.all, 'hierarchy', body] as const,
  matrix: (body: StrategistMatrixRequest) => [...strategistKeys.all, 'matrix', body] as const,
  standardsMatrix: (body: StrategistStandardsMatrixRequest) =>
    [...strategistKeys.all, 'standards-matrix', body] as const,
  transitions: (body: StrategistTransitionsRequest) => [...strategistKeys.all, 'transitions', body] as const,
};

/**
 * Хук списка концептов с агрегатами по выборке (POST /strategist-data/concepts).
 */
export const useStrategistConcepts = (body: StrategistConceptsRequest, enabled = true) => {
  return useQuery({
    queryKey: strategistKeys.concepts(body),
    queryFn: () => fetchConcepts(body),
    placeholderData: keepPreviousData,
    enabled,
  });
};

/**
 * Хук диалогов за срезом (POST /strategist-data/cell-dialogs): влияние среза и
 * список диалогов. Запрос выполняется только когда задан хотя бы один адрес
 * среза — `objectionId`, `argumentId`, `conceptId` или `block`.
 */
export const useStrategistCellDialogs = (body: StrategistCellDialogsRequest) => {
  return useQuery({
    queryKey: strategistKeys.cellDialogs(body),
    queryFn: () => fetchCellDialogs(body),
    enabled: Boolean(body.objectionId || body.argumentId || body.conceptId || body.block),
  });
};

/**
 * Хук карточки понятия (POST /strategist-data/concept): агрегаты и реальные
 * реплики понятия по выборке. Запрос выполняется только когда задан `conceptId`.
 */
export const useStrategistConceptCard = (body: StrategistConceptCardRequest) => {
  return useQuery({
    queryKey: strategistKeys.concept(body),
    queryFn: () => fetchConceptCard(body),
    enabled: Boolean(body.conceptId),
  });
};

/**
 * Хук кластеров понятий (иерархия) выбранной категории на пороге близости
 * (POST /strategist-data/hierarchy).
 */
export const useStrategistHierarchy = (body: StrategistHierarchyRequest, enabled = true) => {
  return useQuery({
    queryKey: strategistKeys.hierarchy(body),
    queryFn: () => fetchHierarchy(body),
    placeholderData: keepPreviousData,
    enabled,
  });
};

/**
 * Хук матрицы «возражение × аргумент» (POST /strategist-data/matrix).
 */
export const useStrategistMatrix = (body: StrategistMatrixRequest, enabled = true) => {
  return useQuery({
    queryKey: strategistKeys.matrix(body),
    queryFn: () => fetchMatrix(body),
    placeholderData: keepPreviousData,
    enabled,
  });
};

/**
 * Хук матрицы «понятие × блок стандарта» (POST /strategist-data/standards-matrix).
 */
export const useStrategistStandardsMatrix = (body: StrategistStandardsMatrixRequest, enabled = true) => {
  return useQuery({
    queryKey: strategistKeys.standardsMatrix(body),
    queryFn: () => fetchStandardsMatrix(body),
    placeholderData: keepPreviousData,
    enabled,
  });
};

/**
 * Хук переходов «возражение → следующий аргумент» (POST /strategist-data/transitions).
 */
export const useStrategistTransitions = (body: StrategistTransitionsRequest, enabled = true) => {
  return useQuery({
    queryKey: strategistKeys.transitions(body),
    queryFn: () => fetchTransitions(body),
    placeholderData: keepPreviousData,
    enabled,
  });
};
